import { useGetDashboardSummary, useGetRecentActivity, useGetPhaseBreakdown, useListTransitions } from "@workspace/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, ArrowUpRight, CheckCircle2, Clock, Users, AlertTriangle, ArrowRight } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from "recharts";
import { Link } from "wouter";
import { format, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

export default function Dashboard() {
  const { data: summary, isLoading: loadingSummary } = useGetDashboardSummary();
  const { data: activity, isLoading: loadingActivity } = useGetRecentActivity();
  const { data: phases, isLoading: loadingPhases } = useGetPhaseBreakdown();
  const { data: activeTransitions, isLoading: loadingActive } = useListTransitions({ status: "active" });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Overview of knowledge continuity operations.</p>
      </div>

      {/* Needs Attention */}
      {Array.isArray(activeTransitions) && activeTransitions.length > 0 && (() => {
        const alerts = activeTransitions
          .map((t) => {
            const daysLeft = differenceInDays(new Date(t.lastDay), new Date());
            const ku = t.knowledgeUnitCount;
            if (daysLeft <= 7 && ku < 10) return { t, reason: "Exit in under a week — knowledge capture critically low", level: "critical" };
            if (daysLeft <= 14 && ku < 10) return { t, reason: "Less than 2 weeks to exit — fewer than 10 knowledge units captured", level: "high" };
            if (daysLeft <= 21 && !t.replacementName) return { t, reason: "Exit in 3 weeks and no replacement assigned yet", level: "medium" };
            return null;
          })
          .filter(Boolean) as { t: any; reason: string; level: string }[];

        if (alerts.length === 0) return null;

        return (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <h2 className="font-semibold text-amber-900">
                {alerts.length} transition{alerts.length > 1 ? "s" : ""} need attention
              </h2>
            </div>
            <div className="space-y-3">
              {alerts.map(({ t, reason, level }) => (
                <div key={t.id} className={cn(
                  "flex items-center justify-between gap-4 p-3 rounded-lg border bg-white",
                  level === "critical" ? "border-red-200" : level === "high" ? "border-amber-300" : "border-amber-200"
                )}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "inline-block w-2 h-2 rounded-full flex-shrink-0",
                        level === "critical" ? "bg-red-500" : level === "high" ? "bg-amber-500" : "bg-amber-400"
                      )} />
                      <Link href={`/transitions/${t.id}`} className="font-medium text-sm hover:underline">
                        {t.employeeName}
                      </Link>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 pl-4">{reason}</p>
                  </div>
                  <Link href={`/transitions/${t.id}`} className="flex-shrink-0 text-xs text-primary hover:underline flex items-center gap-1">
                    View <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Transitions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingSummary ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">{summary?.activeTransitions || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  +{summary?.transitionsThisMonth || 0} this month
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Knowledge Units</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingSummary ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">{summary?.totalKnowledgeUnits || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">Captured across all cases</p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Productivity Boost</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingSummary ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {summary?.hasProductivityData
                    ? `${(summary.avgProductivityImprovement ?? 0).toFixed(1)}%`
                    : "—"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.hasProductivityData
                    ? "Post-transition vs baseline"
                    : "Available after first completed transition"}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Ramp-up Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingSummary ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {summary?.hasRampupData
                    ? `${(summary.avgRampupWeeks ?? 0).toFixed(1)} wks`
                    : "—"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.hasRampupData
                    ? "Replacement to full productivity"
                    : "Measured at handover sign-off"}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        {/* Active Transitions */}
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Active Cases</CardTitle>
            <CardDescription>Currently managed knowledge transfers.</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingActive ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : Array.isArray(activeTransitions) && activeTransitions.length > 0 ? (
              <div className="space-y-4">
                {activeTransitions.slice(0, 5).map((transition) => (
                  <div key={transition.id} className="flex items-center justify-between border-b pb-4 last:border-0 last:pb-0">
                    <div>
                      <Link href={`/transitions/${transition.id}`} className="font-medium hover:underline text-primary">
                        {transition.employeeName}
                      </Link>
                      <div className="text-sm text-muted-foreground">{transition.employeeRole} • {transition.company}</div>
                    </div>
                    <div className="text-right">
                      <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-secondary text-secondary-foreground">
                        {transition.phase}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Exit: {format(new Date(transition.lastDay), "MMM d, yyyy")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Users className="h-8 w-8 text-muted-foreground mb-3 opacity-20" />
                <p className="text-muted-foreground">No active transitions.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Phase Breakdown */}
        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>Pipeline Distribution</CardTitle>
            <CardDescription>Transitions by current phase.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            {loadingPhases ? (
              <Skeleton className="h-[250px] w-full rounded-full" />
            ) : Array.isArray(phases) && phases.length > 0 ? (
              <div className="h-[250px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={phases}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="phase"
                    >
                      {phases.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid hsl(var(--border))' }}
                      itemStyle={{ color: 'hsl(var(--foreground))' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-[250px] items-center justify-center text-muted-foreground">
                No phase data available.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity Feed */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest updates across all transitions.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingActivity ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : Array.isArray(activity) && activity.length > 0 ? (
            <div className="space-y-8">
              {activity.map((item) => (
                <div key={item.id} className="flex gap-4">
                  <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <Activity className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium leading-none mb-1">
                      {item.description}
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground">
                      {item.transitionId && item.employeeName && (
                        <>
                          <Link href={`/transitions/${item.transitionId}`} className="hover:underline hover:text-primary">
                            {item.employeeName}
                          </Link>
                          <span className="mx-1">•</span>
                        </>
                      )}
                      <span>{format(new Date(item.timestamp), "MMM d, h:mm a")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">No recent activity.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
